import React, { useState, useEffect } from 'react';
import { FaShoppingCart, FaHeart as EmptyHeart, FaStar } from 'react-icons/fa';
import { FaHeart as FilledHeart } from 'react-icons/fa6';
import { MdOutlineLocalShipping } from "react-icons/md";
import { BsArrowRepeat } from "react-icons/bs";
import { HiOutlineShoppingBag } from "react-icons/hi";
import Lottie from 'lottie-react';
import slugify from 'slugify';

// Placeholder animation component (you'll need to replace with your actual animation)
const ImagePlaceholder = () => (
  <div className="w-24 h-24 animate-pulse bg-gray-200 rounded"></div>
);

const ProductCard = ({ product, addToCart, toggleFavorite, isFavorite, cartItems = [], navigate }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);

    if (product.image) {
      const img = new Image();
      img.src = product.image;
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageError(true);
    }
  }, [product]);

  const inCart = cartItems?.some(item => item.id === (product.productId || product.id)) || false;

  const handleAddToCartClick = (e) => {
    e.stopPropagation();
    addToCart(product);
  };

  const handleCardClick = () => {
    const titleSlug = slugify(product.name, {
      lower: true,
      strict: true,
      trim: true
    });
    if (navigate) {
      navigate(`/product/${product.id}/${titleSlug}`);
    }
  };

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    toggleFavorite(product.id);
  };

  const isLowStock = product.stock <= 10 && product.stock > 0;
  const isOutOfStock = product.stock === 0;

  const averageRating = product.reviews?.length > 0
    ? (product.reviews.reduce((acc, r) => acc + r.rating, 0) / product.reviews.length)
    : 0;

  const totalReviews = product.reviews?.length || 0;

  return (
    <div
      onClick={handleCardClick}
      className="bg-white border border-gray-200 rounded-lg hover:shadow-lg transition-shadow duration-200 cursor-pointer flex flex-col h-full relative"
    >
      {/* Discount and Popular Badges */}
      {product.discount && (
        <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 rounded text-xs font-semibold z-10">
          {product.discount}
        </div>
      )}
      {product.isPopular && (
        <div className={`absolute top-2 ${product.discount ? 'right-20' : 'right-2'} bg-orange-600 text-white px-2 py-1 rounded text-xs font-semibold z-10`}>
          Popular
        </div>
      )}

      {/* Favorite Button */}
      <button
        onClick={handleFavoriteClick}
        className={`absolute top-10 right-2 text-2xl z-20 transition-colors ${isFavorite(product.id) ? 'text-red-500' : 'text-gray-300'} hover:text-red-400`}
      >
        {isFavorite(product.id) ? <FilledHeart /> : <EmptyHeart />}
      </button>

      {/* Product Image */}
      <div className="w-full h-48 md:h-64 flex items-center justify-center bg-gray-50 border-b border-gray-200">
        {!imageLoaded && !imageError ? (
          <ImagePlaceholder />
        ) : (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-contain p-4"
          />
        )}
      </div>

      {/* Card Content */}
      <div className="p-3 md:p-4 flex flex-col flex-grow">
        {/* Product Name */}
        <h3 className="text-sm md:text-base text-gray-900 font-normal mb-2 line-clamp-2 min-h-[40px]">
          {product.name}
        </h3>

        {/* Rating */}
        <div className="flex items-center gap-2 mb-2">
          <div className="flex items-center text-yellow-500">
            <FaStar className="text-sm" />
            <span className="text-sm text-gray-700 ml-1">
              {averageRating.toFixed(1)}
            </span>
          </div>
          <span className="text-xs text-gray-500">({totalReviews})</span>
        </div>

        {/* Price */}
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-lg md:text-xl font-normal text-gray-900">
            ৳{product.price}
          </span>
          {product.originalPrice && (
            <span className="text-sm text-gray-500 line-through">
              ৳{product.originalPrice}
            </span>
          )}
        </div>

        {/* Stock Info */}
        {isOutOfStock ? (
          <p className="text-sm text-red-600 font-medium mb-2">Out of Stock</p>
        ) : (
          isLowStock && (
            <p className="text-sm text-red-600 font-medium mb-2">
              Only {product.stock} left!
            </p>
          )
        )}

        {/* Meta Info */}
        <div className="flex items-center justify-between text-xs text-gray-600 mb-3 pt-2 border-t border-gray-200">
          <div className="flex items-center gap-1">
            <HiOutlineShoppingBag />
            <span>{product.buyers} Sold</span>
          </div>
        </div>

        {/* Shipping & Return Info */}
        <div className="flex items-center justify-center gap-4 text-xs text-gray-600 mb-3 pt-2 border-t border-gray-200">
          <div className="flex items-center gap-1">
            <MdOutlineLocalShipping className="text-blue-600" />
            <span>Fast Delivery</span>
          </div>
          <div className="flex items-center gap-1">
            <BsArrowRepeat className="text-blue-600" />
            <span>Easy Return</span>
          </div>
        </div>

        {/* Add to Cart Button */}
        <button
          onClick={handleAddToCartClick}
          disabled={isOutOfStock}
          className={`w-full py-2 px-4 rounded text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
            isOutOfStock
              ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
              : inCart
              ? 'bg-orange-500 hover:bg-orange-600 text-white'
              : 'bg-yellow-400 hover:bg-yellow-500 text-gray-900'
          }`}
        >
          {isOutOfStock ? (
            'Out of Stock'
          ) : (
            <>
              <FaShoppingCart />
              {inCart ? 'In Cart' : 'Add to Cart'}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// FavoritesPage Component
function FavoritesPage({ favoriteProducts = {}, products = [], toggleFavorite, addToCart, cartItems = [], navigate }) {
  // favoriteProducts is an object where productId: true/false
  // Filter products array to show only favorited items
  const favoritedItems = products.filter(product => favoriteProducts[product.id]);

  const isFavorite = (productId) => favoriteProducts[productId] === true;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-6 md:py-8 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 md:gap-4">
            <FilledHeart className="w-8 h-8 md:w-10 md:h-10" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">My Wishlist</h1>
              <p className="text-orange-100 mt-1 text-sm md:text-base">
                {favoritedItems.length} {favoritedItems.length === 1 ? 'item' : 'items'} saved for later
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {favoritedItems.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 md:p-16 text-center">
            <EmptyHeart className="mx-auto text-gray-300 mb-4 w-16 h-16 md:w-20 md:h-20" />
            <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mb-2">Your wishlist is empty</h2>
            <p className="text-gray-500 mb-6 text-sm md:text-base">Save items you love to buy them later!</p>
            <button 
              onClick={() => navigate && navigate('/')}
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 md:px-8 py-2.5 md:py-3 rounded-lg font-semibold transition-colors text-sm md:text-base"
            >
              Start Shopping
            </button>
          </div>
        ) : (
          <div>
            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
              <div className="flex items-start gap-2 md:gap-3">
                <div className="text-blue-600 mt-0.5">
                  <svg className="w-4 h-4 md:w-5 md:h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="text-xs md:text-sm text-blue-800">
                  <span className="font-semibold">Price drop alerts enabled!</span> We'll notify you when items on your wishlist go on sale.
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4 lg:gap-6">
              {favoritedItems.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  addToCart={addToCart}
                  toggleFavorite={toggleFavorite}
                  isFavorite={isFavorite}
                  cartItems={cartItems}
                  navigate={navigate}
                />
              ))}
            </div>

            {/* Bottom Info Section */}
            <div className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-gray-200">
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="bg-orange-100 p-2 md:p-3 rounded-lg">
                    <MdOutlineLocalShipping className="w-5 h-5 md:w-6 md:h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm md:text-base">Free Shipping</h3>
                    <p className="text-xs md:text-sm text-gray-600">On orders over ৳500</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-gray-200">
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="bg-blue-100 p-2 md:p-3 rounded-lg">
                    <BsArrowRepeat className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm md:text-base">Easy Returns</h3>
                    <p className="text-xs md:text-sm text-gray-600">30-day return policy</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-gray-200">
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="bg-green-100 p-2 md:p-3 rounded-lg">
                    <svg className="w-5 h-5 md:w-6 md:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm md:text-base">Secure Payment</h3>
                    <p className="text-xs md:text-sm text-gray-600">100% secure transactions</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FavoritesPage;